import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, RefreshCw, Search, ArrowLeft, Eye } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                     "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const DATASET_LABELS = {
  parcels: "Parcels",
  costing: "Costing DP Clients",
  costing_partners: "Costing Partners",
  partners_ledger: "Partners Ledger",
  sales_pending_dp: "Sales Pending DP Clients",
  sales_pending_partners: "Sales Pending Partners",
  errors: "Other Errors",
  payment: "Payment",
};

const formatCell = (v) => {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "number") return v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  return String(v);
};

const isNumericColumn = (col) =>
  /total_cost|shipping_cost|medicine_cost|sell_price|inward_inr|qty|gross_weight/i.test(col);

const YearSummaryPreviewPage = () => {
  const [params] = useSearchParams();
  const year = Number(params.get("year"));
  const month = Number(params.get("month"));
  const dataset = params.get("dataset") || "";
  const clientCode = (params.get("client_code") || "").toUpperCase();
  const day = params.get("day") ? Number(params.get("day")) : null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("asc");

  const toggleSort = (col) => {
    if (sortKey !== col) {
      setSortKey(col);
      setSortDir("asc");
      return;
    }
    if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      // 3rd click clears the sort
      setSortKey("");
      setSortDir("asc");
    }
  };

  useEffect(() => {
    const fetchPreview = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        const qs = new URLSearchParams({
          year: String(year),
          month: String(month),
          dataset,
          limit: "50000",
        });
        if (day) qs.set("day", String(day));
        if (clientCode) qs.set("client_code", clientCode);
        const res = await fetch(
          `${BACKEND_URL}/api/upload-center/year-summary/preview?${qs.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(e.detail || `HTTP ${res.status}`);
        }
        const json = await res.json();
        setData(json);
        document.title = `${json.label} — ${MONTH_NAMES[month - 1]} ${year} · EximFlow`;
      } catch (err) {
        setError(err.message || "Failed to load preview");
      } finally {
        setLoading(false);
      }
    };
    if (!year || month === null || month === undefined || Number.isNaN(month) || !dataset) {
      setError("Missing year / month / dataset in URL");
      setLoading(false);
      return;
    }
    fetchPreview();
  }, [year, month, dataset, clientCode, day]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    const cols = data.columns || [];
    const q = search.trim().toLowerCase();
    let result = q
      ? data.rows.filter((r) =>
          cols.some((c) => String(r?.[c] ?? "").toLowerCase().includes(q)),
        )
      : data.rows.slice();
    if (sortKey) {
      const numeric = isNumericColumn(sortKey);
      const mult = sortDir === "desc" ? -1 : 1;
      result.sort((a, b) => {
        const va = a?.[sortKey];
        const vb = b?.[sortKey];
        if (numeric) {
          return ((Number(va) || 0) - (Number(vb) || 0)) * mult;
        }
        const sa = String(va ?? "").toLowerCase();
        const sb = String(vb ?? "").toLowerCase();
        if (sa < sb) return -1 * mult;
        if (sa > sb) return 1 * mult;
        return 0;
      });
    }
    return result;
  }, [data, search, sortKey, sortDir]);

  const totals = useMemo(() => {
    if (!data) return {};
    const out = {};
    for (const c of data.columns || []) {
      if (isNumericColumn(c)) {
        out[c] = filteredRows.reduce((acc, r) => acc + (Number(r?.[c]) || 0), 0);
      }
    }
    return out;
  }, [data, filteredRows]);

  const downloadExcel = async () => {
    try {
      const token = localStorage.getItem("token");
      const qs = new URLSearchParams({
        year: String(year),
        month: String(month),
        dataset,
      });
      if (day) qs.set("day", String(day));
      if (clientCode) qs.set("client_code", clientCode);
      const res = await fetch(
        `${BACKEND_URL}/api/upload-center/year-summary/download?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `${dataset}_${month}_${year}.xlsx`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      toast.error(`Download failed: ${err.message}`);
    }
  };

  const periodLabel =
    (month && month >= 1 ? `${MONTH_NAMES[month - 1] || "?"} ${year || "?"}` : `FY ${year || "?"}`) +
    (day ? ` · day ${day}` : "") +
    (clientCode ? ` · ${clientCode}` : "");

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-6" data-testid="year-summary-preview-page">
      <Card className="max-w-[1600px] mx-auto">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Link to="/upload-center?tab=year-summary">
                <Button variant="outline" size="sm" data-testid="back-to-year-summary">
                  <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
                  Back
                </Button>
              </Link>
              <CardTitle className="text-lg flex items-center gap-2 text-indigo-700">
                <Eye className="w-5 h-5" />
                {data?.label || DATASET_LABELS[dataset] || dataset}
                <span className="text-sm font-normal text-slate-500">· {periodLabel}</span>
              </CardTitle>
            </div>
            <div className="flex items-center gap-2">
              {data && (
                <div className="text-xs text-slate-600" data-testid="preview-row-counter">
                  <span className="font-semibold text-slate-800">{data.total.toLocaleString()}</span> total
                  {data.truncated && (
                    <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[11px] font-semibold">
                      Truncated at {data.limit.toLocaleString()} — download Excel for the rest
                    </span>
                  )}
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={downloadExcel}
                disabled={!data}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="preview-download-excel"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Download Excel
              </Button>
            </div>
          </div>
          <div className="mt-3 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search across all columns…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border rounded-md focus:outline-none focus:border-indigo-500"
              data-testid="preview-search-input"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-400" />
              <div className="text-sm text-slate-500 mt-3">Loading preview…</div>
              <div className="text-xs text-slate-400 mt-1">
                Larger datasets may take a few seconds.
              </div>
            </div>
          ) : error ? (
            <div className="p-12 text-center text-rose-600 text-sm" data-testid="preview-error">
              {error}
            </div>
          ) : !data || (data.rows || []).length === 0 ? (
            <div className="p-12 text-center text-sm text-slate-500">
              No rows in this slice.
            </div>
          ) : (
            <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 220px)" }}>
              <table className="w-full text-xs" data-testid="preview-table">
                <thead className="bg-slate-100 sticky top-0 z-10 shadow-sm">
                  <tr>
                    {data.columns.map((c) => {
                      const isActive = sortKey === c;
                      const indicator = isActive ? (sortDir === "asc" ? "▲" : "▼") : "↕";
                      return (
                        <th
                          key={c}
                          onClick={() => toggleSort(c)}
                          className={`px-3 py-2 font-semibold whitespace-nowrap border-b cursor-pointer select-none hover:bg-slate-200 ${
                            isActive ? "bg-indigo-100 text-indigo-800" : ""
                          } ${isNumericColumn(c) ? "text-right" : "text-left"}`}
                          title={`Sort by ${c}`}
                          data-testid={`preview-th-${c}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {c}
                            <span className={`text-[10px] ${isActive ? "text-indigo-600" : "text-slate-400"}`}>
                              {indicator}
                            </span>
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-indigo-50/50 border-b border-slate-100"
                    >
                      {data.columns.map((c) => {
                        const rawVal = r?.[c];
                        const display = formatCell(rawVal);
                        const isTracking = c === "tracking" && display;
                        return (
                          <td
                            key={c}
                            className={`px-3 py-1.5 whitespace-nowrap ${
                              isNumericColumn(c) ? "text-right font-mono" : ""
                            }`}
                            title={display}
                          >
                            {isTracking ? (
                              <a
                                href={`/costing?tracking=${encodeURIComponent(display)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 hover:text-indigo-800 hover:underline font-mono"
                                data-testid={`tracking-link-${display}`}
                                title={`Open ${display} in Shipment Costing`}
                              >
                                {display}
                              </a>
                            ) : (
                              display
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-100 sticky bottom-0 border-t-2 border-slate-300">
                  <tr>
                    {data.columns.map((c) => (
                      <td
                        key={c}
                        className={`px-3 py-2 text-xs font-semibold ${
                          isNumericColumn(c) ? "text-right font-mono text-indigo-700" : "text-slate-600"
                        }`}
                      >
                        {isNumericColumn(c) ? (
                          totals[c]?.toLocaleString("en-IN", { maximumFractionDigits: 2 }) || "0"
                        ) : data.columns[0] === c ? (
                          `${filteredRows.length.toLocaleString()} row(s)${search ? ` · matching "${search}"` : ""}`
                        ) : (
                          ""
                        )}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default YearSummaryPreviewPage;
